import React from "react";

/**
 * Formats a stored date for display.
 * @param {string} value the date as `YYYY-MM-DD`.
 * @returns {string} a readable date, or an em dash when unset.
 */
function formatDate(value) {
  if (!value) return "—";
  const date = new Date(`${value}T00:00:00Z`);
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

/**
 * One KPI's due date, showing which scope it was inherited from.
 *
 * Memoised and given primitives plus stable callbacks, since the list commonly
 * runs to several hundred rows.
 * @param {object} props
 * @param {object} props.kpi the KPI.
 * @param {boolean} props.selected whether it is ticked for bulk apply.
 * @param {string|null} props.dueDate the deadline resolved from saved scopes.
 * @param {string|null} props.source which scope that deadline came from.
 * @param {string|null} props.pending an unsaved date staged for this KPI.
 * @param {(kpiId: number) => void} props.onToggle ticks or unticks the row.
 * @param {(kpi: object) => void} props.onConfigure opens the date editor.
 * @param {boolean} props.disabled whether the screen is read-only.
 * @returns {JSX.Element}
 */
function DueDateRow({
  kpi,
  selected,
  dueDate,
  source,
  pending,
  onToggle,
  onConfigure,
  disabled,
}) {
  const effective = pending ?? dueDate;
  const isInherited = !pending && source && source !== "KPI";

  return (
    <div className={`ddt-row${pending ? " ddt-row-pending" : ""}`}>
      <input
        type="checkbox"
        className="ddt-check"
        checked={selected}
        disabled={disabled}
        onChange={() => onToggle(kpi.kpiId)}
        aria-label={`Select ${kpi.title}`}
      />

      <span className="ddt-title" title={kpi.title}>
        {kpi.title}
      </span>

      <span className="ddt-current">
        {formatDate(effective)}
        {isInherited && (
          <span className="ddt-inherited">from {source.toLowerCase()}</span>
        )}
      </span>

      {!disabled && (
        <button
          type="button"
          className="ddt-configure"
          onClick={() => onConfigure(kpi)}
        >
          ⚙ Configure
        </button>
      )}

      {pending && <span className="ddt-badge">Unsaved</span>}
    </div>
  );
}

export default React.memo(DueDateRow);
